import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import { useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'

import { useConfirmation } from '@/contexts/confirmation'
import CommissionPolicyList from '../../CommissionPolicies/List'
import PolicyFormModal from '../../CommissionPolicies/Form'
import { PolicyFormData } from '../../CommissionPolicies/Form/schema'
import { CommissionPolicy } from '@/modules/Affiliate/types'
import {
    createCommissionPolicy,
    updateCommissionPolicy,
    deleteCommissionPolicy,
} from '@/modules/Affiliate/services'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import useAffiliatePolicyPermissions from '../../../hooks/useAffiliatePolicyPermissions'

interface PoliciesProps {
    affiliateId: number
    modalOpen: boolean
    setModalOpen: (open: boolean) => void
}

const Policies: React.FC<PoliciesProps> = ({
    affiliateId,
    modalOpen,
    setModalOpen,
}) => {
    const { t } = useTranslation('affiliate')
    const { setConfirmation } = useConfirmation()
    const queryClient = useQueryClient()
    const { canCreatePolicy, canUpdatePolicy, canDeletePolicy } =
        useAffiliatePolicyPermissions()

    const [selectedPolicy, setSelectedPolicy] =
        useState<CommissionPolicy | null>(null)
    const [isSubmitting, setIsSubmitting] = useState(false)

    // Reset selected policy when modal opens for "Add" action
    useEffect(() => {
        if (modalOpen && selectedPolicy === null) {
            setSelectedPolicy(null)
        }
    }, [modalOpen])

    const handleEditPolicy = (policy: CommissionPolicy) => {
        if (!canUpdatePolicy) return
        setSelectedPolicy(policy)
        setModalOpen(true)
    }

    const handleCloseModal = () => {
        setModalOpen(false)
        // Delay clearing selectedPolicy to avoid UI flash
        setTimeout(() => setSelectedPolicy(null), 200)
    }

    const handleSubmit = async (data: PolicyFormData) => {
        if (selectedPolicy ? !canUpdatePolicy : !canCreatePolicy) return
        setIsSubmitting(true)
        // Format dates to ISO 8601 for Go time.Time parsing
        const effectiveDate = data.effective_date
            ? dayjs(data.effective_date).startOf('day').toISOString()
            : undefined
        const expiredDate = data.expired_date
            ? dayjs(data.expired_date).endOf('day').toISOString()
            : undefined

        try {
            if (selectedPolicy) {
                // This tab also lists GLOBAL policies that merely apply to this affiliate.
                // Editing one here must not quietly demote it to an affiliate-scoped policy,
                // so preserve its ownership instead of forcing [affiliateId].
                const ownership = selectedPolicy.is_global
                    ? { is_global: true, affiliate_ids: [] }
                    : {
                          is_global: false,
                          affiliate_ids: selectedPolicy.affiliate_ids?.length
                              ? selectedPolicy.affiliate_ids
                              : [affiliateId],
                      }

                await updateCommissionPolicy({
                    policy_id: selectedPolicy.policy_id,
                    payload: {
                        ...ownership,
                        scope: data.scope,
                        product_group_id: data.product_group_id,
                        product_id: data.product_id,
                        policy_name: data.policy_name,
                        commission_percent: data.commission_percent,
                        effective_date: effectiveDate,
                        expired_date: expiredDate,
                        priority: data.priority,
                        description: data.description || undefined,
                    },
                })
                toast.success(t('policy_updated_successfully'))
            } else {
                // Policies created from an affiliate's own tab always target that affiliate.
                await createCommissionPolicy({
                    is_global: false,
                    affiliate_ids: [affiliateId],
                    policy_name: data.policy_name,
                    scope: data.scope,
                    commission_percent: data.commission_percent,
                    product_group_id: data.product_group_id,
                    product_id: data.product_id,
                    effective_date: effectiveDate,
                    expired_date: expiredDate,
                    priority: data.priority,
                    description: data.description || undefined,
                })
                toast.success(t('policy_created_successfully'))
            }
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.AFFILIATE_COMMISSION_POLICIES],
            })
            handleCloseModal()
        } catch (error) {
            console.error('Failed to save policy:', error)
            toast.error(t('save_policy_error'))
        } finally {
            setIsSubmitting(false)
        }
    }

    const handleDeletePolicy = (policy: CommissionPolicy) => {
        if (!canDeletePolicy) return
        setConfirmation({
            open: true,
            title: t('delete_policy'),
            description: t('confirm_delete_policy'),
            onConfirm: async () => {
                try {
                    await deleteCommissionPolicy(policy.policy_id)
                    toast.success(t('policy_deleted_successfully'))
                    queryClient.invalidateQueries({
                        queryKey: [QUERY_KEYS.AFFILIATE_COMMISSION_POLICIES],
                    })
                } catch (error) {
                    console.error('Failed to delete policy:', error)
                    toast.error(t('save_policy_error'))
                }
            },
        })
    }

    const handleTogglePolicy = async (policy: CommissionPolicy) => {
        if (!canUpdatePolicy) return
        try {
            await updateCommissionPolicy({
                policy_id: policy.policy_id,
                payload: { is_active: !policy.is_active },
            })
            toast.success(t('policy_updated_successfully'))
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.AFFILIATE_COMMISSION_POLICIES],
            })
        } catch (error) {
            console.error('Failed to toggle policy:', error)
            toast.error(t('save_policy_error'))
        }
    }

    return (
        <>
            <CommissionPolicyList
                affiliateId={affiliateId}
                onEditPolicy={handleEditPolicy}
                onDeletePolicy={handleDeletePolicy}
                onTogglePolicy={handleTogglePolicy}
                canUpdatePolicy={canUpdatePolicy}
                canDeletePolicy={canDeletePolicy}
            />
            {(canCreatePolicy || canUpdatePolicy) && (
                <PolicyFormModal
                    open={modalOpen}
                    onClose={handleCloseModal}
                    policy={selectedPolicy}
                    onSubmit={handleSubmit}
                    isLoading={isSubmitting}
                    fixedAffiliateId={affiliateId}
                />
            )}
        </>
    )
}

export default Policies
